<script setup>
/**
 * Imports
 */
import { computed, watch } from "vue";
import { useRoute } from "vue-router";

/**
 * Props & Emits
 */
const props = defineProps({
  data: Object,
  sublink: Boolean,
});

const emit = defineEmits(["toggle"]);

/**
 * Variables
 */
const currentRoute = useRoute();

/**
 * Computed
 */
const tag = computed(() => (props.data.sublinks?.length ? "button" : "router-link"));

/**
 * Methods
 */
function isActive(link) {
  const route = link || props.data;
  if (!route?.name) {
    return false;
  }
  if (route.activeNames) {
    return route.activeNames.includes(currentRoute.name);
  }
  return route.name === currentRoute.name;
}

/**
 * Watchers
 */
watch(
  currentRoute,
  () => {
    const route = props.data;
    if (!route.expanded && route.sublinks && route.sublinks.some((r) => isActive(r))) {
      emit("toggle", true);
    }
  },
  {
    immediate: true,
  }
);
</script>

<template>
  <component
    :is="tag"
    class="aside__link"
    :class="{
      'aside__link--active': isActive(),
      'aside__link--sublink': sublink,
    }"
    :to="{ name: data.name }"
    @click="emit('toggle')"
  >
    <IconComponent class="aside__link-icon" :name="data.icon" />
    <span class="aside__link-title">{{ data.title }}</span>
    <span v-if="data.badge" class="aside__link-badge">{{ data.badge > 99 ? "99+" : data.badge }}</span>
    <IconComponent
      v-if="data.sublinks && data.sublinks.length"
      class="aside__link-arrow"
      :class="{ 'aside__link-arrow--rotated': data.expanded }"
      name="chevron-down"
    />
  </component>
</template>

<style lang="stylus">
.aside__link {
	display: flex;
	align-items: center;
	gap: 10px
	padding: 10px
	border-radius 12px
	cursor: pointer;
	position: relative;
	color: var(--gray)
	width 100%
	background none
	border: none
	transition var(--transition)

	&:hover:not(&--active) {
		//background-color: var(--black-o03)
		background-color: var(--gray-light)
		color: var(--white)
	}

	&--active {
		background-color: var(--black-2)
		color: var(--white)
		border-radius: 12px
	}

	&--sublink {
		padding-left 44px
	}

	&-icon {
		width: 24px;
		height: 24px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&-title {
		font-size: 14px;
		font-weight: 500;
		flex-grow: 1;
		text-align left
	}

	&-badge {
		min-width 20px
		height 20px
		padding: 0 6px
		border-radius 10px
		background var(--red)
		color var(--white)
		font-size 0.6875rem
		font-weight 600
		display inline-flex
		align-items center
		justify-content center
		flex-shrink 0
	}

	&-arrow {
		width: 24px;
		height: 24px;
		transition: transform 0.2s ease;

		&--rotated {
			transform: rotate(-180deg);
		}

		svg {
			width: 16px;
			height: 16px;
		}
	}
}
</style>
